import { LabelWithMessage } from '@common/LabelWithMessage';
import { ICompanyType } from '@company/companyType/companyType.models';
import { getCompanyTypeNameByCompanyTypeId } from '@company/companyType/companyType.services';
import { IProjectCompany } from '@company/projectCompany/projectCompany.models';
import { Badge, ComponentType, FormController, FormFieldMethods, Icon, Input, Tooltip } from '@gcl/react-core';
import { IProjectRole, IProjectRoleOption } from '@role/projectRole/projectRole.models';
import { useGetRolesProjectRequest } from '@role/projectRole/projectRole.queries';
import { UserTable } from '@userProfile/list/UserTable/UserTable';
import { IProjectUserProfile } from '@userProfile/projectUserProfile.models';
import { useMemo } from 'react';
import { EProjectRequestFormKeys, IProjectRequestFormProjectCompany } from './projectRequest.models';
import './ProjectRequestDetail.scss';

interface IProjectRequestDetailCompanyUserProps {
  company: IProjectRequestFormProjectCompany;
  companyType?: ICompanyType;
  onClickOpenMemberModal: (e: any, company: IProjectCompany, companyIndex: number, user?: IProjectUserProfile) => void;

  onDeleteCompany: (company: IProjectCompany) => void;
  onEditCompany: (e: any, company: IProjectCompany) => void;
  mainFormFieldMethods: FormFieldMethods;
  index: number;
  isReadOnly: boolean;
}

export const ProjectRequestDetailCompanyUser = ({
  company,
  onClickOpenMemberModal,
  onDeleteCompany,
  companyType,
  onEditCompany,
  mainFormFieldMethods,
  index,
  isReadOnly,
}: IProjectRequestDetailCompanyUserProps) => {
  const mapRolesWithUsers = mainFormFieldMethods.watch(
    `${EProjectRequestFormKeys.COMPANIES}[${index}].${EProjectRequestFormKeys.ROLES}`,
  );

  const {
    formState: { isDirty },
  } = mainFormFieldMethods;

  const { data: projectRolesOfCompany } = useGetRolesProjectRequest(
    {
      companyTypeId: company.companyTypeId,
      organizationTypeId: company.organizationTypeId,
    },
    !isDirty,
  );

  const projectRolesWithUsers: IProjectRole[] = useMemo(() => {
    return ((projectRolesOfCompany ?? []) as IProjectRoleOption[]).map((prc) => {
      return { ...prc, users: mapRolesWithUsers ? mapRolesWithUsers[prc.roleId] : [], rowVersion: '' };
    });
  }, [JSON.stringify(projectRolesOfCompany), JSON.stringify(mapRolesWithUsers)]);

  const onOpenMemberModal = (e: any, projectUserProfile?: IProjectUserProfile) => {
    onClickOpenMemberModal(e, company, index, projectUserProfile);
  };

  const content = useMemo(() => {
    return (
      <div className="gcl-gems-project-request-detail-company-container">
        <div className="gcl-gems-project-request-detail-company-add ">
          <LabelWithMessage labelFor={company.code ?? ''} text={company.name ?? ''} />

          {companyType?.allowCreation && !isReadOnly && (
            <Tooltip overlay={`Edit ${company?.name}`} id="gcl-gems-user-table-edit-company-btn-tooltip">
              <Icon
                iconName="Pencil"
                className="gcl-gems-user-list-company-icon"
                onClick={(e) => {
                  onEditCompany(e, company);
                }}
              />
            </Tooltip>
          )}

          {!isReadOnly && (
            <Tooltip overlay={`Add ${company.name} Member`} id="gcl-gems-user-table-add-company-member-btn-tooltip">
              <Icon
                data-testid={`add-${company.code}`}
                iconName="PersonPlus"
                className="gcl-gems-user-list-company-icon"
                onClick={(e) => {
                  onClickOpenMemberModal(e, company, index);
                }}
              />
            </Tooltip>
          )}

          {!company.isDefault && companyType?.allowCreation && !isReadOnly && (
            <Tooltip overlay={`Delete ${company?.name}`} id="gcl-gems-user-table-edit-company-btn-tooltip">
              <Icon
                iconName="Trash"
                className="gcl-gems-user-list-company-icon"
                onClick={(e) => {
                  onDeleteCompany(company);
                }}
              />
            </Tooltip>
          )}
          <Badge pill type={ComponentType.Secondary} className="gcl-ml-xs">
            {getCompanyTypeNameByCompanyTypeId(company.companyTypeId)}
          </Badge>
        </div>
        {(projectRolesWithUsers?.length ?? 0) > 0 && (
          <UserTable
            teamDisplayMode="projectRequect"
            hasEditRight={!isReadOnly}
            roles={projectRolesWithUsers ?? []}
            openMemberModal={onOpenMemberModal}
            renderRoleHeader={(role: IProjectRole) => {
              return (
                <FormController
                  formFieldMethods={mainFormFieldMethods}
                  renderInputControl={({ field, fieldState, formState, id, setValue }) => {
                    return (
                      <div>
                        <Input
                          className="gcl-gems-project-request-user-table-role-header-hidden-input"
                          inputRef={field.ref}
                          onChange={(newValue) => {}}
                        />
                        <LabelWithMessage
                          labelFor={`${EProjectRequestFormKeys.COMPANIES}[${index}].${EProjectRequestFormKeys.ROLES}.${role.roleId}`}
                          text={role.role?.name ?? role.name ?? ''}
                          required
                        />
                      </div>
                    );
                  }}
                  rules={{
                    validate: (value: any) => {
                      if (value && typeof value === 'object' && value.length > 0) return true;
                      return 'At least one should be selected';
                    },
                  }}
                  id={`${EProjectRequestFormKeys.COMPANIES}[${index}].${EProjectRequestFormKeys.ROLES}.${role.roleId}`}
                />
              );
            }}
          />
        )}
      </div>
    );
  }, [JSON.stringify(company), JSON.stringify(projectRolesWithUsers), onClickOpenMemberModal]);
  return content;
};
